import React, { useEffect, useState } from "react";

import _ from "lodash";
import moment, { Moment } from "moment";
import { useTranslation } from "react-i18next";

import { ExclamationCircleTwoTone } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Row, Select, Spin } from "antd";

import { NumberInput } from "../../../../../components/inputs";
import { PlaceSelector } from "../../../../../components/selectors";
import { availableTripClass, defaultHost, marketToHostMap, TripClass } from "../../../../../store/common_types";

export interface Passengers {
  adults?: number;
  children?: number;
  infants?: number;
}

export interface SearchFormErrors {
  origin?: string[];
  destination?: string[];
  departureDate?: string[];
  returnDate?: string[];

  adults?: string[];
  children?: string[];
  infants?: string[];

  tripClass?: string[];
  locale?: string[];
  currency?: string[];
  market?: string[];
}

export interface SearchFormProps extends Passengers {
  className?: string;

  origin?: string;
  destination?: string;
  departureDate?: Moment;
  returnDate?: Moment;

  tripClass?: TripClass;
  locale?: string;
  currency?: string;
  market?: string;

  errors?: SearchFormErrors;
  onFinish?: (value: SearchFormResult) => void;
  isLoading?: boolean;
}

export interface SearchFormResult {
  origin: string;
  destination: string;
  departureDate: Moment;
  returnDate?: Moment;

  adults: number;
  children: number;
  infants: number;

  tripClass: TripClass;
  locale?: string;
  currency?: string;
  market?: string;

  url: string;
}

export const SearchForm: React.FC<SearchFormProps> = (props) => {
  const { t } = useTranslation("debug_page");
  const maxTotalPassengers = 9;
  const [state, setState] = useState({
    origin: props.origin,
    destination: props.destination,
    departureDate: props.departureDate,
    returnDate: props.returnDate,

    current: {
      adults: props.adults ?? 1,
      children: props.children ?? 0,
      infants: props.infants ?? 0,
    },
    max: {
      adults: maxTotalPassengers,
      children: maxTotalPassengers,
      infants: (props.adults ?? 1) < 6 ? props.adults ?? 1 : 6,
    },

    tripClass: (props.tripClass ?? "Y") as TripClass,
    locale: props.locale ?? "ru",
    currency: props.currency ?? "rub",
    market: props.market ?? "ru",

    errors: props.errors ?? {},
  });

  useEffect(() => {
    setState((c) => ({ ...c, errors: props.errors ?? {} }));
  }, [props.errors]);

  const handleValuesChange = (values: {}) => {
    setState((currentState) => {
      if (_.isEmpty(currentState.errors)) {
        return currentState;
      }
      const errors = _.keys(currentState.errors).reduce((current, field) => {
        if (field in values) {
          return current;
        }
        return { ...current, [field]: currentState.errors[field as keyof SearchFormErrors] };
      }, {});
      return { ...currentState, errors };
    });
  };

  const handleFinish = (form: SearchFormResult) => {
    if (_.isEmpty(state.errors) && props.onFinish) {
      const result = Object.keys(form).reduce((res, field) => {
        const value = form[field as keyof SearchFormResult];
        if (value) return { ...res, [field]: value };
        return res;
      }, {} as SearchFormResult);
      props.onFinish(result);
    }
  };

  const checkPassengers = ({ field }: any, value: number) => {
    setState((currentState) => {
      const passengers = { ...currentState.current, [field]: value };
      const maxPassengers = { adults: 9, children: 9, infants: passengers.adults < 6 ? passengers.adults : 6 };
      const total = (_.keys(currentState.current) as (keyof Passengers)[]).reduce((sum, key) => {
        return sum + (key === field ? value : currentState.current[key]);
      }, 0);
      return { ...currentState, current: passengers, max: total === maxTotalPassengers ? passengers : maxPassengers };
    });
    return Promise.resolve();
  };

  const handleChangeMarket = (market: string) => setState((currentState) => ({ ...currentState, market }));

  const handleChangeDepartureDate = (value: Moment | null, dateStr: string) => {
    setState((currentState) => ({ ...currentState, departureDate: value ?? undefined }));
  };
  const disabledDepartureDate = (current: Moment): boolean => {
    return current.isBefore(moment.utc().startOf("date").subtract(1, "day"));
  };
  const disabledReturnDate = (current: Moment): boolean => {
    return state.departureDate ? current.isBefore(state.departureDate) : false;
  };

  return (
    <Spin spinning={props.isLoading ?? false}>
      <Form
        name="debug-search"
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
        className={props.className}
      >
        <Form.Item
          label={t("forms.search.origin.label")}
          name="origin"
          validateTrigger={["onFocus", "onChange"]}
          rules={[{ required: true, message: t("forms.search.origin.message") }]}
          initialValue={state.origin}
          validateStatus={state.errors.origin ? "error" : undefined}
          help={state.errors.origin}
        >
          <PlaceSelector showArrow={false} />
        </Form.Item>
        <Form.Item
          label={t("forms.search.destination.label")}
          name="destination"
          validateTrigger={["onFocus", "onChange"]}
          rules={[{ required: true, message: t("forms.search.destination.message") }]}
          initialValue={state.destination}
          validateStatus={state.errors.destination ? "error" : undefined}
          help={state.errors.destination}
        >
          <PlaceSelector showArrow={false} />
        </Form.Item>
        <Row>
          <Form.Item
            label={t("forms.search.departureDate.label")}
            name="departureDate"
            validateTrigger={["onFocus", "onChange"]}
            initialValue={state.departureDate}
            rules={[{ required: true, message: t("forms.search.departureDate.message") }]}
            style={{ width: "calc(50% - 5px)" }}
            validateStatus={state.errors.departureDate ? "error" : undefined}
            help={state.errors.departureDate}
          >
            <DatePicker
              style={{ width: "100%" }}
              onChange={handleChangeDepartureDate}
              disabledDate={disabledDepartureDate}
            />
          </Form.Item>
          <Form.Item
            label={t("forms.search.returnDate.label")}
            name="returnDate"
            validateTrigger={["onFocus", "onChange"]}
            initialValue={state.returnDate}
            tooltip={t("forms.search.returnDate.help")}
            style={{ width: "calc(50% - 5px)" }}
            validateStatus={state.errors.returnDate ? "error" : undefined}
            help={state.errors.returnDate}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabled={!state.departureDate}
              disabledDate={disabledReturnDate}
              defaultPickerValue={state.departureDate}
            />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item
            label={t("forms.search.adults.label")}
            name="adults"
            tooltip={t("forms.search.adults.help")}
            validateTrigger={["onFocus", "onChange"]}
            initialValue={state.current.adults}
            rules={[{ required: true, message: t("forms.search.adults.message"), validator: checkPassengers }]}
            validateStatus={state.errors.adults ? "error" : undefined}
            help={state.errors.adults}
          >
            <NumberInput min={1} max={state.max.adults} />
          </Form.Item>
          <Form.Item
            label={t("forms.search.children.label")}
            name="children"
            tooltip={t("forms.search.children.help")}
            rules={[{ validator: checkPassengers }]}
            initialValue={state.current.children}
            validateStatus={state.errors.children ? "error" : undefined}
            help={state.errors.children}
          >
            <NumberInput min={0} max={state.max.children} />
          </Form.Item>
          <Form.Item
            label={t("forms.search.infants.label")}
            name="infants"
            tooltip={t("forms.search.infants.help")}
            rules={[{ validator: checkPassengers }]}
            initialValue={state.current.infants}
            validateStatus={state.errors.infants ? "error" : undefined}
            help={state.errors.infants}
          >
            <NumberInput
              min={0}
              max={state.max.infants}
              incrementDisabledMessage={t("forms.search.infants.incrementDisabledMessage")}
            />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item
            label={t("forms.search.tripClass.label")}
            name="tripClass"
            rules={[{ required: true, message: t("forms.search.tripClass.message") }]}
            initialValue={props.tripClass ?? "Y"}
            style={{ width: "calc(38% - 5px)" }}
            validateStatus={state.errors.tripClass ? "error" : undefined}
            help={state.errors.tripClass}
          >
            <Select>
              {availableTripClass.map((tripClass) => (
                <Select.Option key={`trip-class-${tripClass}`} value={tripClass}>
                  {t(`forms.search.tripClass.options.${tripClass}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={t("forms.search.locale.label")}
            name="locale"
            style={{ width: "calc(18% - 5px)" }}
            initialValue={state.locale}
            validateStatus={state.errors.locale && "error"}
            tooltip={{
              title: t("forms.search.locale.help"),
              ...(state.errors.locale && {
                title: state.errors.locale,
                color: "rgba(255,0,0,0.7)",
                icon: <ExclamationCircleTwoTone twoToneColor="red" />,
              }),
            }}
          >
            <Input
              style={{ textTransform: "lowercase" }}
              placeholder={t("forms.search.locale.placeholder")}
              autoComplete="off"
              maxLength={2}
            />
          </Form.Item>
          <Form.Item
            label={t("forms.search.currency.label")}
            name="currency"
            style={{ width: "calc(22% - 5px)" }}
            initialValue={state.currency}
            validateStatus={state.errors.currency && "error"}
            tooltip={{
              title: t("forms.search.currency.help"),
              ...(state.errors.currency && {
                title: state.errors.currency,
                color: "rgba(255,0,0,0.7)",
                icon: <ExclamationCircleTwoTone twoToneColor="red" />,
              }),
            }}
          >
            <Input
              style={{ textTransform: "uppercase" }}
              placeholder={t("forms.search.currency.placeholder")}
              autoComplete="off"
              maxLength={3}
            />
          </Form.Item>
          <Form.Item
            name="market"
            initialValue={state.market}
            style={{ width: "calc(20% - 5px)" }}
            label={t("forms.search.market.label")}
            validateStatus={state.errors.market && "error"}
            tooltip={{
              title: t("forms.search.market.help", { host: marketToHostMap.get(state.market!) || defaultHost }),
              overlayInnerStyle: { whiteSpace: "pre-line" },
              ...(state.errors.market && {
                title: state.errors.market,
                color: "rgba(255,0,0,0.7)",
                icon: <ExclamationCircleTwoTone twoToneColor="red" />,
              }),
            }}
          >
            <Select showSearch notFoundContent={null} onChange={handleChangeMarket} onSearch={handleChangeMarket}>
              {state.market && (
                <Select.Option key={`market-${state.market}`} value={state.market}>
                  {state.market.toUpperCase()}
                </Select.Option>
              )}

              {[...marketToHostMap.keys()]
                .filter((market) => market.toLowerCase().localeCompare(state.market.toLowerCase()))
                .map((market) => (
                  <Select.Option key={`market-${market}`} value={market}>
                    {market.toUpperCase()}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Row>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            {t("forms.search.submit")}
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};
